import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { IntlContext } from "context";
import { Button } from "components/ui/button";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { buildRoute, ROUTES } from "routes";
import { useLoginDialog } from "context/LoginDialog";
import { useProviderProfileCompletion } from "app/Profile/useProviderProfileCompletion";
import { PROFILE_COMPLETION_SECTION_ID } from "app/Profile/profileCompletion";

export type AdPrimaryActionProps = {
    isOwnAd: boolean;
    isProvider: boolean;
    alreadyApplied: boolean;
    hasService: boolean;
    applicationsCount: number;
    adId: string;
    adClientId: string;
    serviceId: string;
};

const AdPrimaryAction: React.FC<AdPrimaryActionProps> = ({
    isProvider,
    isOwnAd,
    alreadyApplied,
    hasService,
    applicationsCount,
    adId,
    adClientId,
    serviceId,
}) => {
    const intl = useContext(IntlContext);

    const {
        actions: { openLogin },
    } = useLoginDialog();
    const profileCompletion = useProviderProfileCompletion();
    const isProfileComplete = Boolean(profileCompletion?.requiredComplete);

    if (isOwnAd) {
        return (
            <>
                <p className="text-center text-sm text-slate-600">{intl.formatMessage("ad.view.own_ad_notice")}</p>
                <Button asChild size="lg" className="xl:h-14">
                    <Link to={buildRoute.myAdEdit(adId)}>{intl.formatMessage("ad.view.edit_ad")}</Link>
                </Button>
                {applicationsCount > 0 && (
                    <Button asChild variant="outline">
                        <Link to={buildRoute.myAdApplications(adId)}>
                            {intl.formatMessage("ad.view.see_applications", { count: applicationsCount })}
                        </Link>
                    </Button>
                )}
            </>
        );
    }

    if (alreadyApplied) {
        return (
            <div className="w-full h-14 bg-emerald-50 rounded-[20px] flex items-center justify-center gap-2 text-emerald-700 text-[15px] font-black">
                <Icon icon={{ prefix: "fas", iconName: "check" }} aria-hidden="true" />
                {intl.formatMessage("ad.view.application_sent")}
            </div>
        );
    }

    if (isProvider && !isProfileComplete) {
        return (
            <div className="flex flex-col gap-2">
                <p className="text-[13px] text-[#5a607c] font-medium text-center">
                    {intl.formatMessage("ad.view.profile_incomplete")}
                </p>
                <Button asChild size="lg" className="xl:h-14">
                    <Link to={`${ROUTES.ACCOUNT_PROFILE}#${PROFILE_COMPLETION_SECTION_ID}`}>
                        {intl.formatMessage("ad.view.profile_incomplete_button")}
                    </Link>
                </Button>
            </div>
        );
    }

    if (isProvider && hasService && isProfileComplete) {
        return (
            <Button asChild size="lg" className="xl:h-14">
                <Link to={buildRoute.messagingOffer(serviceId, adClientId, adId)}>
                    {intl.formatMessage("ad.view.apply_to_ad")}
                </Link>
            </Button>
        );
    }

    if (isProvider && !hasService) {
        return (
            <div className="flex flex-col gap-2">
                <p className="text-[13px] text-[#5a607c] font-medium text-center">
                    {intl.formatMessage("ad.view.service_required")}
                </p>
                <Button asChild size="lg" className="xl:h-14">
                    <Link to={ROUTES.MY_SERVICES}>{intl.formatMessage("service.label.add_activate")}</Link>
                </Button>
            </div>
        );
    }

    // Visitor or unauthenticated user
    return (
        <Button type="button" size="lg" className="xl:h-14" onClick={() => openLogin()}>
            {intl.formatMessage("ad.view.apply_to_ad")}
        </Button>
    );
};

export default AdPrimaryAction;
